import React, { useEffect, useState } from 'react';
import { DefaultButton, Label, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { buildColiboRedirectUri } from 'features/Rooms/PersonalDashboard/ColiboWidget/colibo.helpers';
import type { IColiboConfiguration } from './colibo.types';

interface IColiboSettingsFormProps {
    coliboBaseUrl: string;
    coliboClientId: string;
    hasStoredSecret: boolean;
    isOpen: boolean;
    updateColiboConfig: (config: IColiboConfiguration) => Promise<OrganizationDto>;
    onSaveSuccess: () => void;
    onCancel: () => void;
}

const looksLikeHttpsUrl = (value: string): boolean => {
    try {
        return new URL(value.trim()).protocol === 'https:';
    } catch {
        return false;
    }
};

// Shared by the save path (here) and ColiboSettings.tsx's deactivation path.
export function resolveColiboErrorMessage(error: unknown): string {
    const status =
        typeof error === 'object' && error !== null && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;
    // 400 = the backend's model validation refused the URL, the client id or a missing secret.
    if (status === 400) return IvicosStrings.coliboSettingsInvalidError;
    return IvicosStrings.somethingWentWrong;
}

export const ColiboSettingsForm: React.FC<IColiboSettingsFormProps> = ({
    coliboBaseUrl,
    coliboClientId,
    hasStoredSecret,
    isOpen,
    updateColiboConfig,
    onSaveSuccess,
    onCancel
}) => {
    const [baseUrl, setBaseUrl] = useState(coliboBaseUrl);
    const [clientId, setClientId] = useState(coliboClientId);
    const [clientSecret, setClientSecret] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Discard unsaved drafts when the panel closes, and re-sync once org data arrives.
    useEffect(() => {
        if (!isOpen) {
            setErrorMessage(null);
            setIsSaving(false);
        }
        setBaseUrl(coliboBaseUrl);
        setClientId(coliboClientId);
        setClientSecret('');
    }, [isOpen, coliboBaseUrl, coliboClientId]);

    const handleSave = async (): Promise<void> => {
        if (!looksLikeHttpsUrl(baseUrl) || !clientId.trim() || (!hasStoredSecret && !clientSecret.trim())) {
            setErrorMessage(IvicosStrings.coliboSettingsInvalidError);
            return;
        }
        setIsSaving(true);
        setErrorMessage(null);
        try {
            await updateColiboConfig({
                is_colibo_enable: true,
                colibo_base_url: baseUrl.trim(),
                colibo_client_id: clientId.trim(),
                ...(clientSecret.trim() ? { colibo_client_secret: clientSecret.trim() } : {})
            });
            onSaveSuccess();
        } catch (error) {
            console.error('Failed to save Colibo settings:', error);
            setErrorMessage(resolveColiboErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', minHeight: 'calc(100vh - 110px)' } }}>
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Label>{IvicosStrings.coliboSettingsBaseUrlLabel}</Label>
                    <TextField
                        type="url"
                        value={baseUrl}
                        placeholder="https://intranet.colibo.com"
                        description={IvicosStrings.coliboSettingsBaseUrlHint}
                        onChange={(_ev, newValue) => setBaseUrl(newValue ?? '')}
                        data-test-id="colibo-base-url"
                    />
                </Stack>
                <TextField
                    label={IvicosStrings.coliboSettingsRedirectUriLabel}
                    value={buildColiboRedirectUri(window.location.origin)}
                    readOnly
                    data-test-id="colibo-redirect-uri"
                />
                <TextField
                    label={IvicosStrings.coliboSettingsClientIdLabel}
                    value={clientId}
                    onChange={(_ev, newValue) => setClientId(newValue ?? '')}
                    data-test-id="colibo-client-id"
                />
                <TextField
                    label={IvicosStrings.coliboSettingsClientSecretLabel}
                    type="password"
                    canRevealPassword
                    value={clientSecret}
                    description={hasStoredSecret ? IvicosStrings.coliboSettingsClientSecretKeepHint : IvicosStrings.coliboSettingsClientSecretHint}
                    onChange={(_ev, newValue) => setClientSecret(newValue ?? '')}
                    data-test-id="colibo-client-secret"
                />
                {errorMessage && (
                    <Text style={{ color: '#A80000' }} data-test-id="colibo-settings-error">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', paddingBottom: 8 } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <DefaultButton
                        text={IvicosStrings.coliboSettingsCancelButton}
                        onClick={onCancel}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="colibo-cancel-button"
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <PrimaryButton
                        text={IvicosStrings.coliboSettingsSaveButton}
                        onClick={handleSave}
                        disabled={isSaving || !baseUrl.trim() || !clientId.trim()}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="colibo-save-button"
                    />
                </Stack.Item>
            </Stack>
        </Stack>
    );
};
